import { metadataNodeFields, type CorpusNode } from './model'
import { findNode } from './treeUtils'

// Folding a pulled tree into the corpus. A full pull rebuilds the tree from
// the cloud's rows; a delta pull applies changed rows to the tree in hand,
// last write (by HLC) wins. Both leave the Trash and the open page to the
// store (see applyRemoteTree).

function remoteSubtype(n: any): 'desk' | 'folder' | 'page' {
  return n.subtype || (n.type === 'folder' ? 'folder' : 'page')
}

/** Sorts each level of a tree by position, in place. */
function sortLevel(list: CorpusNode[]) {
  list.sort((a, b) => (a.position || 0) - (b.position || 0))
  for (const node of list) {
    if (node.children) sortLevel(node.children)
  }
}

/**
 * The tree a full pull describes, from its live rows. Expanded state is kept
 * from `current` for nodes it already has.
 */
export function buildRemoteTree(activeNodes: any[], current: CorpusNode[]): CorpusNode[] {
  const nodeMap = new Map<string, CorpusNode>()
  const roots: CorpusNode[] = []

  // 1. Create CorpusNode items (preserve existing expanded state if known)
  for (const n of activeNodes) {
    const existing = findNode(current, n.id)
    const subtype = remoteSubtype(n)
    nodeMap.set(n.id, {
      id: n.id,
      name: n.name || 'Untitled',
      subtype,
      position: n.position || 0,
      hlcTimestamp: n.hlcTimestamp || 0,
      icon: n.icon || null,
      color: n.color || null,
      ...metadataNodeFields(n.metadata),
      children: [],
      ...(subtype !== 'desk' ? { expanded: existing?.expanded ?? n.expanded ?? true } : {}),
    })
  }

  // 2. Build tree hierarchy
  for (const n of activeNodes) {
    const item = nodeMap.get(n.id)
    if (!item) continue
    if (n.parentId && nodeMap.has(n.parentId)) {
      const parent = nodeMap.get(n.parentId)!
      parent.children = parent.children || []
      parent.children.push(item)
      if (parent.subtype === 'page' && parent.expanded === undefined) parent.expanded = true
    } else {
      roots.push(item)
    }
  }

  // 3. Sort by position
  sortLevel(roots)
  return roots
}

/**
 * `current` with a delta pull's rows applied: tombstones removed, changed
 * nodes updated in place when their HLC is at least as new, new nodes added,
 * and the hierarchy reassembled and sorted.
 */
export function mergeRemoteDelta(current: CorpusNode[], remoteNodes: any[]): CorpusNode[] {
  // 1. Flatten current tree into a map of { node, parentId }
  const existingMap = new Map<string, { node: CorpusNode; parentId: string | null }>()
  const flattenTree = (nodes: CorpusNode[], parentId: string | null = null) => {
    for (const node of nodes) {
      existingMap.set(node.id, { node, parentId })
      if (node.children && node.children.length > 0) {
        flattenTree(node.children, node.id)
      }
    }
  }
  flattenTree(current)

  // 2. Apply delta changes
  for (const n of remoteNodes) {
    if (n.deletedAt) {
      // Tombstone: remove from existing map
      existingMap.delete(n.id)
      continue
    }

    const subtype = remoteSubtype(n)
    const isContainer = subtype === 'desk' || subtype === 'folder'

    if (existingMap.has(n.id)) {
      const entry = existingMap.get(n.id)!
      const existing = entry.node
      // LWW: apply if incoming HLC is >= existing HLC
      if (!existing.hlcTimestamp || (n.hlcTimestamp && n.hlcTimestamp >= existing.hlcTimestamp)) {
        existing.name = n.name || 'Untitled'
        existing.subtype = subtype
        existing.position = n.position !== undefined ? n.position : existing.position
        existing.hlcTimestamp = n.hlcTimestamp || existing.hlcTimestamp
        existing.icon = n.icon !== undefined ? n.icon : existing.icon
        existing.color = n.color !== undefined ? n.color : existing.color
        if (n.metadata !== undefined) Object.assign(existing, metadataNodeFields(n.metadata))
        entry.parentId = n.parentId !== undefined ? n.parentId : entry.parentId
        if ((isContainer || subtype === 'page') && !existing.children) {
          existing.children = []
        }
      }
    } else {
      // New node
      const newNode: CorpusNode = {
        id: n.id,
        name: n.name || 'Untitled',
        subtype,
        position: n.position || 0,
        hlcTimestamp: n.hlcTimestamp || 0,
        icon: n.icon || null,
        color: n.color || null,
        ...metadataNodeFields(n.metadata),
        children: [],
        ...(subtype !== 'desk' ? { expanded: n.expanded ?? true } : {}),
      }
      existingMap.set(n.id, { node: newNode, parentId: n.parentId ?? null })
    }
  }

  // 3. Clear existing children and reassemble hierarchy
  for (const entry of existingMap.values()) {
    if (entry.node.children) {
      entry.node.children = []
    }
  }

  const roots: CorpusNode[] = []
  for (const entry of existingMap.values()) {
    const item = entry.node
    const parentId = entry.parentId
    if (parentId && existingMap.has(parentId)) {
      const parent = existingMap.get(parentId)!.node
      parent.children = parent.children || []
      parent.children.push(item)
      if (parent.subtype === 'page' && parent.expanded === undefined) parent.expanded = true
    } else {
      roots.push(item)
    }
  }

  // 4. Sort by position at every level
  sortLevel(roots)
  return roots
}
